import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "./MyTechProfileTopbar.css";

const MyTechProfileTopbar = ({ sectionsRef }) => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const [selected, setSelected] = useState("Home");
  const [isExpanded, setIsExpanded] = useState(false);

  // Dynamically create menuItems based on profile data
  const menuItems = useMemo(() => {
    let baseItems = ["Home", "Skills"];

    if (profile?.experiences) {
      baseItems.push("Experiences");
    }

    baseItems.push("Projects", "Certifications");

    if (profile?.technicalAchievements) {
      baseItems.push("Achievements");
    }

    baseItems.push("Contact");

    return baseItems;
  }, [profile]);

  const isMobile = window.innerWidth <= 768;
  const displayedItems =
    isMobile && !isExpanded ? menuItems.slice(0, 3) : menuItems;

  // Intersection Observer for scroll spy
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px", // Trigger when section is in the top 30% of viewport
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Find which section is intersecting and update selected state
          const sectionKey = Object.keys(sectionsRef).find(
            (key) => sectionsRef[key].current === entry.target
          );

          if (sectionKey) {
            // Capitalize first letter to match menu item format
            const menuItemName =
              sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
            setSelected(menuItemName);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all section refs
    Object.values(sectionsRef).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Cleanup
    return () => {
      Object.values(sectionsRef).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionsRef, menuItems]);

  useEffect(() => {
    const handleScroll = () => {
      if (isExpanded) {
        setIsExpanded(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isExpanded]);

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const handleNavClick = (item) => {
    setSelected(item);
    const ref = sectionsRef[item.toLowerCase()];

    if (ref && ref.current) {
      if (isMobile) {
        setIsExpanded(false);
      }

      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  return (
    <div className="mytechprofile-topbar">
      <motion.nav
        className={`mytechprofile-nav ${
          isExpanded ? "mytechprofile-nav-expanded" : ""
        }`}
        initial="hidden"
        animate="visible"
        variants={navVariants}
      >
        <AnimatePresence>
          {displayedItems.map((item) => (
            <motion.a
              key={item}
              href={`#${item.toLowerCase()}`}
              className={`mytechprofile-nav-item ${
                selected === item ? "mytechprofile-nav-selected" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item);
              }}
              variants={itemVariants}
              layout
            >
              {item}
            </motion.a>
          ))}
        </AnimatePresence>
        {isMobile && (
          <div className="mytechprofile-expand-toggle">
            {isExpanded ? (
              <KeyboardArrowUpIcon onClick={() => setIsExpanded(false)} />
            ) : (
              <KeyboardArrowDownIcon onClick={() => setIsExpanded(true)} />
            )}
          </div>
        )}
      </motion.nav>
    </div>
  );
};

export default MyTechProfileTopbar;
